import React, {
  createContext,
  useEffect,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageProvider';
import { DabsiApp } from '../types/App';
import { COLOR_MAP, makeTransparent } from '../utils/color';
import { useOrgContext } from './org/OrgProvider';
import { BetaBadge } from './shared/BetaBadge';
import {
  IconBell,
  IconMessage,
  IconMessages,
  IconSelector,
  IconSparkles,
  IconTruck,
} from '@tabler/icons-react';

type ContextProps = {
  app: DabsiApp;
  setApp: (value: DabsiApp) => void;
};

const defaultContext: ContextProps = {
  app: 'clientell',
  setApp: () => {},
};

const AppSwitcherContext = createContext(defaultContext);

const ALL_APPS: DabsiApp[] = [
  'clientell',
  'imminote',
  'botmaker',
  'rakine',
  'whatchat',
];

const APP_PATTERN = ALL_APPS.join('|');
export const APP_ORG_REGEX = new RegExp(`^\\/(${APP_PATTERN})\\/orgs\\/(\\d+)`);

const getAppFromPath = (path: string): DabsiApp | null => {
  const match = path.match(APP_ORG_REGEX);
  return (match?.[1] as DabsiApp | undefined) ?? null;
};

const getStoredApp = (): DabsiApp => {
  const storedApp = localStorage.getItem('selectedApp');
  return storedApp && ALL_APPS.includes(storedApp as DabsiApp)
    ? (storedApp as DabsiApp)
    : 'clientell';
};

export const AppSwitcher = () => {
  const navigate = useNavigate();
  const { messages } = useLanguage();
  const { org, orgApps } = useOrgContext();
  const { app, setApp } = useAppSwitcherContext();
  const [open, setOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const allAppDetails: Record<
    DabsiApp,
    {
      icon: React.ReactNode;
      background: string;
      name: string;
      description: string;
      badge?: React.ReactNode;
    }
  > = useMemo(
    () => ({
      clientell: {
        icon: <IconMessages size={24} />,
        background: makeTransparent(COLOR_MAP.clientell),
        name: messages.header.clientell,
        description: messages.header.clientellDescription,
      },
      imminote: {
        icon: <IconBell size={24} />,
        background: makeTransparent(COLOR_MAP.imminote),
        name: messages.header.imminote,
        description: messages.header.imminoteDescription,
      },
      botmaker: {
        icon: <IconSparkles size={24} />,
        background: makeTransparent(COLOR_MAP.botmaker),
        name: messages.header.botmaker,
        description: messages.header.botmakerDescription,
        badge: <BetaBadge />,
      },
      rakine: {
        icon: <IconTruck size={24} />,
        background: makeTransparent(COLOR_MAP.rakine),
        name: messages.header.rakine,
        description: messages.header.rakineDescription,
        badge: <BetaBadge />,
      },
      whatchat: {
        icon: <IconMessage size={24} />,
        background: makeTransparent(COLOR_MAP.whatchat),
        name: messages.header.whatchat,
        description: messages.header.whatchatDescription,
        badge: <BetaBadge />,
      },
    }),
    [messages]
  );

  const appDetails = useMemo(
    () =>
      Object.fromEntries(
        orgApps
          .filter(appName => appName in allAppDetails)
          .map(appName => [appName, allAppDetails[appName]])
      ) as Partial<typeof allAppDetails>,
    [orgApps, allAppDetails]
  );

  const handleChange = useCallback(
    (selectedApp: DabsiApp) => {
      if (!org) return;
      setApp(selectedApp);
      setOpen(false);
      localStorage.setItem('selectedApp', selectedApp);
      navigate(`/${selectedApp}/orgs/${org.id}`);
    },
    [setApp, navigate, org]
  );

  const currentDetails = appDetails[app];

  return (
    <div ref={selectRef} style={{ position: 'relative', width: '260px' }}>
      <button
        onClick={() => setOpen(prev => !prev)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          width: '100%',
          padding: '8px 12px',
          background: 'rgba(0, 0, 0, 0.01)',
          border: `1px solid ${makeTransparent(COLOR_MAP[app], 0.3)}`,
          borderRadius: '8px',
          boxShadow:
            '0px 4px 6px rgba(0, 0, 0, 0.1), 0px 1px 3px rgba(0, 0, 0, 0.06)',
          cursor: 'pointer',
          maxHeight: '56px',
          textAlign: 'left',
        }}
      >
        {currentDetails && (
          <>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                backgroundColor: currentDetails.background,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {currentDetails.icon}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                <span
                  style={{
                    fontWeight: 500,
                    fontSize: '14px',
                    color: '#333',
                  }}
                >
                  {currentDetails.name}
                </span>
                {currentDetails.badge}
              </div>
              <div
                style={{
                  fontSize: '12px',
                  color: '#666',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {currentDetails.description}
              </div>
            </div>
          </>
        )}
        <IconSelector
          style={{ fontSize: '20px', color: '#666', flexShrink: 0 }}
        />
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1300,
            backgroundColor: '#fff',
            borderRadius: '8px',
            boxShadow:
              '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
            padding: '4px 0',
            marginTop: '4px',
          }}
        >
          {Object.keys(appDetails).map(key => {
            const details = appDetails[key as DabsiApp];
            if (!details) return null;
            const { icon, background, name, description, badge } = details;
            return (
              <button
                key={key}
                onClick={() => handleChange(key as DabsiApp)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                  padding: '10px 16px',
                  border: 'none',
                  background: key === app ? '#f5f5f5' : 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
                onMouseEnter={e =>
                  (e.currentTarget.style.backgroundColor = '#f5f5f5')
                }
                onMouseLeave={e =>
                  (e.currentTarget.style.backgroundColor =
                    key === app ? '#f5f5f5' : '')
                }
              >
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    backgroundColor: background,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 500,
                        fontSize: '14px',
                        color: '#333',
                      }}
                    >
                      {name}
                    </span>
                    {badge}
                  </div>
                  <div
                    style={{
                      fontSize: '12px',
                      color: '#666',
                    }}
                  >
                    {description}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const AppSwitcherProvider = ({ children }: { children: ReactNode }) => {
  const [app, setApp] = useState<DabsiApp>(getStoredApp);
  const { org, orgApps, orgAppsLoaded } = useOrgContext();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isOrgPath = APP_ORG_REGEX.test(pathname);
  const routeApp = getAppFromPath(pathname);
  const showSwitcher = isOrgPath && orgAppsLoaded && orgApps.length > 0;

  useEffect(() => {
    if (!isOrgPath || !org?.id || !orgAppsLoaded) return;

    if (routeApp && orgApps.includes(routeApp)) {
      if (app !== routeApp) {
        setApp(routeApp);
      }
      if (localStorage.getItem('selectedApp') !== routeApp) {
        localStorage.setItem('selectedApp', routeApp);
      }
      return;
    }

    if (orgApps.includes(app)) {
      if (localStorage.getItem('selectedApp') !== app) {
        localStorage.setItem('selectedApp', app);
      }
      return;
    }

    const fallbackApp = orgApps[0];
    if (!fallbackApp) return;

    setApp(fallbackApp);
    localStorage.setItem('selectedApp', fallbackApp);
    navigate(`/${fallbackApp}/orgs/${org.id}`, { replace: true });
  }, [app, isOrgPath, navigate, org?.id, orgApps, orgAppsLoaded, routeApp]);

  const value = {
    app,
    setApp,
  };

  return (
    <AppSwitcherContext.Provider value={value}>
      {children}
    </AppSwitcherContext.Provider>
  );
};

export const useAppSwitcherContext = () => useContext(AppSwitcherContext);
